// Downscale + compress an image File to a small webp Blob in the browser before
// upload. Keeps KV storage tiny and the public site fast, and means the worker
// never has to do (paid) image processing. EXIF orientation is respected so
// phone photos aren't sideways.

function loadImageEl(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Falha ao carregar a imagem"));
    };
    img.src = url;
  });
}

export async function resizeImage(
  file: File,
  maxDim = 600,
  quality = 0.82
): Promise<Blob> {
  if (!file.type.startsWith("image/")) {
    throw new Error("O arquivo selecionado não é uma imagem");
  }

  // Prefer createImageBitmap (handles EXIF orientation); fall back to <img>.
  let source: ImageBitmap | HTMLImageElement;
  let w: number;
  let h: number;
  try {
    source = await createImageBitmap(file, {
      imageOrientation: "from-image",
    } as ImageBitmapOptions);
    w = source.width;
    h = source.height;
  } catch {
    const img = await loadImageEl(file);
    source = img;
    w = img.naturalWidth;
    h = img.naturalHeight;
  }

  let tw = w;
  let th = h;
  if (Math.max(w, h) > maxDim) {
    const scale = maxDim / Math.max(w, h);
    tw = Math.round(w * scale);
    th = Math.round(h * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas não suportado neste navegador");
  ctx.drawImage(source as CanvasImageSource, 0, 0, tw, th);
  if ("close" in source && typeof source.close === "function") source.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    // webp where supported; browsers without webp encoding silently emit png,
    // which the worker also accepts.
    canvas.toBlob(resolve, "image/webp", quality)
  );
  if (!blob) throw new Error("Falha ao processar a imagem");
  return blob;
}
